import React from 'react'
import useSWRMutation from 'swr/mutation'
import { useSWRConfig } from 'swr'

import Input from '../ui/input'
import FieldLabel from '../ui/input/FieldLabel'
import Button, { ButtonSize, ButtonVariant } from '../ui/button'
import { Client, TClientForm } from '../../types'
import { useAlerts } from '../../context/alerts'
import { checkTradingNameExists, saveOrUpdateClient } from '../../api'
import { revalidateClientCaches } from '../../lib/revalidateClientCaches'


export type SavedClientSnapshot = Pick<Client, '_id' | 'tradingName' | 'email' | 'billingAddress'>

type Props = {
  defaultValues?: TClientForm
  onSave: (clientId?: string, savedClient?: SavedClientSnapshot) => void | Promise<void>
  onCancel?: () => void
  saveLabel?: string
  cancelLabel?: string
  silent?: boolean // if true no alert is shown after save
  defaultPristine?: boolean // if true save button is always enabled
  /** When true, only trading name + email show until the user expands optional fields (e.g. add-client modal on invoice). */
  collapseOptionalFields?: boolean
  /** Which field receives focus when the form mounts (create flow defaults to trading name). */
  initialFocus?: 'tradingName' | 'email'
  saveVariant?: ButtonVariant
  saveAndSendLabel?: string
  saveAndSendDisabled?: boolean
  onSaveAndSend?: (clientId?: string, clientEmail?: string) => void | Promise<void>
}
/**
 * Form to create/edit a client.
 */
const emptyValues: TClientForm = {
  'client.tradingName': '',
  'client.billingAddress': '',
  'client.name': '',
  'client.lastName': '',
  'client.email': ''
}

const resolveSavedClientId = (
  response: { data?: Record<string, unknown> } | Record<string, unknown> | null | undefined,
  editingClientId?: string,
): string | undefined => {
  const data = (response && 'data' in response ? response.data : response) as Record<string, unknown> | undefined
  const raw = data?.insertedId ?? data?._id ?? editingClientId
  if (raw == null || raw === '') return undefined
  const id = String(raw)
  return id === '[object Object]' ? undefined : id
}

const buildSavedClientSnapshot = (
  response: { data?: Record<string, unknown> } | null | undefined,
  formValues: TClientForm,
  clientId: string,
): SavedClientSnapshot => {
  const doc = response?.data
  return {
    _id: clientId,
    tradingName: String(doc?.tradingName ?? formValues['client.tradingName'] ?? '').trim(),
    email: String(doc?.email ?? formValues['client.email'] ?? '').trim(),
    billingAddress: String(doc?.billingAddress ?? formValues['client.billingAddress'] ?? '').trim(),
  }
}
const ClientForm: React.FC<Props> = ({
  silent,
  defaultValues = emptyValues,
  onSave,
  onCancel,
  saveLabel = 'Save',
  cancelLabel = 'Cancel',
  defaultPristine = true,
  collapseOptionalFields = false,
  initialFocus = 'tradingName',
  saveVariant = ButtonVariant.PRIMARY,
  saveAndSendLabel,
  saveAndSendDisabled = false,
  onSaveAndSend,
}) => {
  const { mutate } = useSWRConfig()
  const { trigger: triggerSaveOrUpdate, isMutating: isLoading } = useSWRMutation(
    '@internal/save-client',
    (_key, { arg }: { arg: { body: any } }) => saveOrUpdateClient('/api/clients', { arg }),
  )
  const formRef = React.useRef<HTMLFormElement>(null)
  const saveModeRef = React.useRef<'save' | 'saveAndSend'>('save')
  const [saveMode, setSaveMode] = React.useState<'save' | 'saveAndSend'>('save')

  const [values, setValues] = React.useState(defaultValues)
  const [pristine, setPristine] = React.useState(defaultPristine)
  const [tradingNameError, setTradingNameError] = React.useState<string | null>(null)
  const [isCheckingTradingName, setIsCheckingTradingName] = React.useState(false)
  const [optionalExpanded, setOptionalExpanded] = React.useState(() => {
    if (!collapseOptionalFields) return true
    const v = defaultValues
    return Boolean(
      String(v['client.name'] || '').trim()
        || String(v['client.lastName'] || '').trim()
        || String(v['client.billingAddress'] || '').trim()
    )
  })
  const { addAlert } = useAlerts()

  const tradingName = `${values['client.tradingName'] || ''}`.trim()
  /** When editing, `parseClientToForm` sets `_id` on the form values (not `client._id`). */
  const editingClientId = values._id

  React.useEffect(() => {
    if (!tradingName) {
      setTradingNameError(null)
      setIsCheckingTradingName(false)
      return
    }
    const timer = setTimeout(async () => {
      setIsCheckingTradingName(true)
      const exists = await checkTradingNameExists(tradingName, editingClientId)
      setTradingNameError(exists ? 'A client with this trading name already exists' : null)
      setIsCheckingTradingName(false)
    }, 350)
    return () => clearTimeout(timer)
  }, [tradingName, editingClientId])

  const handleSubmit = async (e: any) => {
    e.preventDefault()
    if (tradingNameError || isCheckingTradingName) return
    try {
      const submitBody = editingClientId
        ? values
        : (() => {
            const { _id: _omit, ...rest } = values
            return rest
          })()
      const response = await triggerSaveOrUpdate({ body: submitBody })
      setPristine(true)
      const submitMode = saveModeRef.current
      if (!silent && submitMode === 'save') {
        addAlert({ type: 'info', message: 'Client has been saved' })
      }
      const clientId = resolveSavedClientId(
        response,
        editingClientId ? String(editingClientId) : undefined,
      )
      const savedClient = clientId ? buildSavedClientSnapshot(response, values, clientId) : undefined
      const clientEmail = String(values['client.email'] || '').trim()
      if (submitMode === 'saveAndSend' && onSaveAndSend) {
        await onSaveAndSend(clientId, clientEmail)
      } else {
        await onSave(clientId, savedClient)
      }
      await revalidateClientCaches(mutate)
      saveModeRef.current = 'save'
      setSaveMode('save')
    } catch (error: any) {
      saveModeRef.current = 'save'
      setSaveMode('save')
      const message = `${error?.message || ''}`
      const isDuplicateTradingName =
        message.includes('A client with that trading name already exists')
        || message.includes('E11000')
      if (isDuplicateTradingName) {
        setTradingNameError('A client with this trading name already exists')
        return
      }
      addAlert({ type: 'error', message: message || 'Could not save client' })
    }
  }

  const isSavingAndSending = isLoading && saveMode === 'saveAndSend'
  const saveDisabled = isLoading || pristine || !!tradingNameError || isCheckingTradingName

  const requestSubmit = (mode: 'save' | 'saveAndSend') => {
    saveModeRef.current = mode
    setSaveMode(mode)
    formRef.current?.requestSubmit()
  }

  const handleTextChange = (e: any) => {
    const { name, value } = e.target
    setPristine(false)
    setValues({ ...values, [name]: value })
  }

  return (
    (
      <div className='row'>
        <div className='col'>
          <form ref={formRef} onSubmit={handleSubmit}>
            <div className='mb-3'>
              <div className='d-flex align-items-baseline gap-2 flex-wrap'>
                <FieldLabel label='Trading Name' htmlFor='client.tradingName' required className='mb-0' />
                <div
                  className='small text-end flex-shrink-0'
                  style={{ minHeight: '1.25em', maxWidth: 'min(100%, 18rem)' }}
                  aria-live='polite'
                >
                  {tradingNameError ? (
                    <span className='text-danger'>{tradingNameError}</span>
                  ) : isCheckingTradingName ? (
                    <span className='text-muted'>Checking...</span>
                  ) : (
                    <span className='invisible user-select-none' aria-hidden>&nbsp;</span>
                  )}
                </div>
              </div>
              <input
                autoFocus={initialFocus === 'tradingName'}
                className={`form-control form-control-md ${tradingNameError ? 'is-invalid' : ''}`}
                placeholder='Your client trading name'
                id='client.tradingName'
                defaultValue={values['client.tradingName']}
                name='client.tradingName'
                onChange={handleTextChange}
                required
              />
            </div>
            <div className='mb-3'>
              <Input
                label='Email'
                id='client.email'
                name='client.email'
                placeholder='Your client email'
                defaultValue={values['client.email']}
                onChange={handleTextChange}
                autoFocus={initialFocus === 'email'}
                type='email'
                optional
                tooltip='Add an address to send invoices and overdue reminders by email.'
              />
            </div>
            {collapseOptionalFields && (
              <div className='mb-3'>
                <button
                  type='button'
                  className='btn btn-link p-0 text-decoration-underline'
                  onClick={() => setOptionalExpanded((o) => !o)}
                  aria-expanded={optionalExpanded}
                >
                  {optionalExpanded ? 'Hide optional fields' : 'See optional fields'}
                </button>
              </div>
            )}
            {(!collapseOptionalFields || optionalExpanded) && (
              <>
                <div className='row g-2 mb-3'>
                  <div className='col-12 col-sm-6'>
                    <Input
                      label='Contact Name'
                      id='client.name'
                      placeholder='Mick'
                      name='client.name'
                      defaultValue={values['client.name']}
                      onChange={handleTextChange}
                      optional
                    />
                  </div>
                  <div className='col-12 col-sm-6'>
                    <Input
                      label='Contact Last Name'
                      id='client.lastName'
                      placeholder='Jagger'
                      name='client.lastName'
                      defaultValue={values['client.lastName']}
                      onChange={handleTextChange}
                      optional
                    />
                  </div>
                </div>
                <div className='mb-3'>
                  <Input
                    label='Billing Address'
                    placeholder='Victoria St. 23'
                    id='client.billingAddress'
                    name='client.billingAddress'
                    defaultValue={values['client.billingAddress']}
                    onChange={handleTextChange}
                    optional
                    tooltip='This billing address will be shown in the invoices.'
                  />
                </div>
              </>
            )}
            <div className='d-flex justify-content-between pt-4'>
              {onCancel && (
                <div>
                  <Button disabled={isLoading} size={ButtonSize.LARGE} variant={ButtonVariant.LIGHT} type='button' onClick={onCancel}>{cancelLabel}</Button>
                </div>
              )}
              <div className='d-flex gap-2 justify-content-end'>
                <Button
                  size={ButtonSize.LARGE}
                  variant={saveVariant}
                  loading={isLoading && saveMode === 'save'}
                  loadingText='Saving'
                  type='button'
                  disabled={saveDisabled}
                  onClick={() => requestSubmit('save')}
                >
                  {saveLabel}
                </Button>
                {onSaveAndSend && saveAndSendLabel && (
                  <Button
                    size={ButtonSize.LARGE}
                    variant={ButtonVariant.PRIMARY}
                    loading={isSavingAndSending}
                    loadingText='Saving'
                    type='button'
                    disabled={saveDisabled || saveAndSendDisabled}
                    onClick={() => requestSubmit('saveAndSend')}
                  >
                    {saveAndSendLabel}
                  </Button>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>
    )
  )
}

export default ClientForm
